import React, { useEffect, useReducer, useRef } from 'react';
import { useForm } from 'react-final-form';

import { SvgIcon } from 'js/app/components/common/SvgIcon';

import { clsx } from 'js/app/utils/clsx.util';
import { useClickOutSide } from 'js/app/utils/common.util';
import { addZeroToStart } from 'js/app/utils/methods.util';
import { SELECT_ARROW_ICON } from 'js/components/common/Fields/Select/constants.buzz.js';

import { initialData, reducer, SET_DAY, SET_FULL_DATE, SET_MONTH, SET_YEAR } from '../../reducer';

import style from './InputDateForAndroid.scss';

const renderYears = (withCurrentYear) => {
    const lifeSpan = 100;
    const currentYear = new Date().getFullYear();
    const minYear = currentYear - lifeSpan;
    let year = withCurrentYear ? currentYear : currentYear - 18;
    const years = [];

    for (; year >= minYear; year--) {
        years.push(<option key={year} value={year}>{year}</option>);
    }

    return years;
};

const WrapSelect = ({ children, className }) => (
    <div className={clsx(style.wrapSelect, className)}>
        {children}

        <SvgIcon type={SELECT_ARROW_ICON} className={style.arrow} />
    </div>
);

export const InputDateForAndroid = ({
    meta: { active, touched, submitError, error = submitError } = {},
    name,
    value,
    withCurrentYear
}) => {
    const form = useForm();
    const formRef = useRef(null);
    const [state, dispatch] = useReducer(reducer, initialData);
    const isError = error && touched && !active;

    useEffect(() => {
        form.change(name, state.fullDate);
    }, [state.fullDate, form, name]);

    useEffect(() => {
        if (value && !state.fullDate) {
            dispatch({ type: SET_FULL_DATE, payload: value });
        }
    }, [state.fullDate, value]);

    const handlerOnChange = (type) => (event) => {
        dispatch({ type, payload: event.target.value });
    };

    const handlerOnBlur = () => {
        form.blur(name);
    };

    useClickOutSide(formRef, handlerOnBlur);

    return (
        <div ref={formRef} className={clsx(style.container, { [style.isError]: isError })}>
            <WrapSelect className={style.isDay}>
                <select
                    value={state.day}
                    className={clsx(style.select, { [style.isValue]: state.day })}
                    onChange={handlerOnChange(SET_DAY)}
                    data-testid="InputDateForAndroid_day"
                >
                    <option value="DD" className="hidden">DD</option>

                    {Array.from({ length: 31 }, (_, index) => {
                        const day = addZeroToStart(index + 1);

                        return <option key={index} value={day}>{day}</option>;
                    })}
                </select>
            </WrapSelect>

            <WrapSelect className={style.isMonth}>
                <select
                    value={state.month}
                    className={clsx(style.select, { [style.isValue]: state.month })}
                    onChange={handlerOnChange(SET_MONTH)}
                    data-testid="InputDateForAndroid_month"
                >
                    <option value="DD" className="hidden">MM</option>

                    {Array.from({ length: 12 }, (_, index) => {
                        const month = addZeroToStart(index + 1);

                        return <option key={index} value={month}>{month}</option>;
                    })}
                </select>
            </WrapSelect>

            <WrapSelect className={style.isYear}>
                <select
                    value={state.year}
                    className={clsx(style.select, { [style.isValue]: state.year })}
                    onChange={handlerOnChange(SET_YEAR)}
                    data-testid="InputDateForAndroid_year"
                >
                    <option value="YYYY" className="hidden">YYYY</option>

                    {renderYears(withCurrentYear)}
                </select>
            </WrapSelect>
        </div>
    );
};
